import React from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import { NamedLink, PaginationLinks, IconArrowHead } from '../../../components';
import { createResourceLocatorString } from '../../../util/routes';
import routeConfiguration from '../../../routeConfiguration';
import classNames from 'classnames';

import Post from './Post/Post';
import SortBy from './SortBy/SortBy';
import css from './Posts.module.css';

// Icons as separate components
const NoPostsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 256 256">
    <path
      fill="#FF6B3D"
      strokeMiterlimit="10"
      d="M83.2 217.6c-24.742 0-44.8-20.058-44.8-44.8V83.2c0-24.742 20.058-44.8 44.8-44.8h89.6c24.742 0 44.8 20.058 44.8 44.8v89.6c0 24.742-20.058 44.8-44.8 44.8z"
      fontFamily="none"
      fontSize="none"
      fontWeight="none"
      style={{ mixBlendMode: 'normal' }}
      textAnchor="none"
    />
    <g
      fill="none"
      stroke="#0e0f0c"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit="10"
      fontFamily="none"
      fontSize="none"
      fontWeight="none"
      style={{ mixBlendMode: 'normal' }}
      textAnchor="none"
    >
      <path
        strokeWidth="18"
        d="M182.857 145.145h40.777c10.1 0 18.286 8.187 18.286 18.286v37.358c.003 17.962-13.04 33.267-30.775 36.114l-10.697 1.719a457.422 457.422 0 0 1-144.896 0l-10.697-1.72c-17.735-2.846-30.778-18.151-30.775-36.113V163.43c0-10.099 8.187-18.286 18.286-18.286h40.777c10.094 0 18.03 8.594 22.491 17.646 4.828 9.856 14.5 18.926 32.366 18.926 17.847 0 27.538-9.07 32.366-18.926 4.461-9.07 12.397-17.646 22.491-17.646zM167.68 73.447c-7.9 15.89-23.79 31.762-39.68 39.698-15.872-7.936-31.744-23.808-39.68-39.68M128 11.605v101.12"
      />
    </g>
  </svg>
);

const SearchErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 256 256">
    <path
      fill="#FF6B3D"
      strokeMiterlimit="10"
      d="M83.2 217.6c-24.742 0-44.8-20.058-44.8-44.8V83.2c0-24.742 20.058-44.8 44.8-44.8h89.6c24.742 0 44.8 20.058 44.8 44.8v89.6c0 24.742-20.058 44.8-44.8 44.8z"
      fontFamily="none"
      fontSize="none"
      fontWeight="none"
      style={{ mixBlendMode: 'normal' }}
      textAnchor="none"
    />
    <g
      fill="none"
      stroke="#0e0f0c"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeMiterlimit="10"
      fontFamily="none"
      fontSize="none"
      fontWeight="none"
      style={{ mixBlendMode: 'normal' }}
      textAnchor="none"
    >
      <path
        strokeWidth="18"
        d="M40.96 48.252c20.195-3.282 37.176-8.15 87.04-8.15 38.383 0 68.944 3.593 87.795 6.598 12.453 1.985 21.056 12.874 21.056 25.483V179.47c0 14.502-11.32 26.442-25.61 28.92-3.179.55-6.331 1.132-9.606 1.717"
      />
      <path
        strokeWidth="18"
        d="M19.148 102.482h18.71m3.458-54.194a30.524 30.524 0 0 1 2.235-.468c2.48-.422 4.934-.865 7.431-1.316C67.748 43.48 86.467 40.102 128 40.102c39.062 0 70.023 3.72 88.786 6.757 11.855 1.918 20.065 12.294 20.065 24.304v31.319H87.181M156.21 168.25h36.874"
      />
      <path
        strokeWidth="18"
        d="M10.057 10.058c79.759 102.168 131.527 153.384 235.886 235.884M19.148 102.486v76.986c0 14.502 11.323 26.443 25.613 28.92 18.673 3.239 36.527 7.51 83.239 7.51 1.444 0 2.86-.003 4.25-.012"
      />
    </g>
  </svg>
);

// Loading state component
const LoadingState = () => <div className={css.searchInProgressWrapper} />;

// Error state component
const ErrorState = () => (
  <div className={css.searchErrorWrapper}>
    <div className={css.searchError}>
      <SearchErrorIcon className={css.searchErrorIcon} />
      <h2 className={css.searchErrorTitle}>
        <FormattedMessage id="CommunityPage.searchPostTitle" />
      </h2>
      <p className={css.searchErrorText}>
        <FormattedMessage id="CommunityPage.searchPostText" />
      </p>
    </div>
  </div>
);

// Empty state component
const EmptyState = ({ returnLink, postsCategory }) => (
  <div className={css.noPostsWrapper}>
    {returnLink}
    <div className={css.noPosts}>
      <NoPostsIcon className={css.noPostsIcon} />
      <h2 className={css.noPostsTitle}>
        {postsCategory ? (
          <FormattedMessage
            id="CommunityPage.noPostsTitleWithCategory"
            values={{ category: postsCategory.label }}
          />
        ) : (
          <FormattedMessage id="CommunityPage.noPostsTitle" />
        )}
      </h2>
      <p className={css.noPostsText}>
        <FormattedMessage id="CommunityPage.noPostsText" />
      </p>
    </div>
  </div>
);

// Return link component
const ReturnLink = () => (
  <NamedLink className={css.returnLink} name="CommunityPage">
    <IconArrowHead className={css.returnLinkIcon} direction="left" />
    <FormattedMessage id="CommunityPage.returnLink" />
  </NamedLink>
);

/**
 * Active-filters pill row — sits between the sort header and the post
 * list. Only renders when at least one filter is applied. Each pill
 * removes its own param on click; "Clear all" wipes every supported
 * filter at once while preserving sort/page params.
 *
 * Reads directly from `currentSearchParams` so it stays in sync with
 * sidebar chip selections without any extra state plumbing.
 */
const FILTER_QUERY_PARAMS = [
  { param: 'pub_category', optionsKey: 'categories', labelKey: 'CommunityPage.categories' },
  { param: 'pub_topics', optionsKey: 'topics', labelKey: 'CommunitySidebar.topicsTitle' },
  { param: 'pub_discipline', optionsKey: 'disciplines', labelKey: 'CommunitySidebar.disciplinesTitle' },
  { param: 'pub_role', optionsKey: 'roles', labelKey: 'CommunitySidebar.rolesTitle' },
];

const ActiveFilters = ({ intl, currentSearchParams, history, optionGroups }) => {
  // Resolve each URL value into one or more { groupLabel, key, label }
  // pills. Multi-enum values come through as comma-joined lists.
  const pills = [];
  for (const { param, optionsKey, labelKey } of FILTER_QUERY_PARAMS) {
    const raw = currentSearchParams?.[param];
    if (!raw) continue;
    const options = optionGroups[optionsKey] || [];
    const groupLabel = intl.formatMessage({ id: labelKey });
    raw
      .split(',')
      .filter(Boolean)
      .forEach(key => {
        const match = options.find(o => o.key === key);
        pills.push({ param, key, groupLabel, label: match?.label || key });
      });
  }

  if (pills.length === 0) return null;

  const pushSearch = newParams => {
    history.push(
      createResourceLocatorString('CommunityPage', routeConfiguration(), {}, newParams)
    );
  };

  // Removing a single pill from a multi-enum keeps the other keys
  // in the comma-list; from a single-enum removes the whole param.
  const removePill = (param, key) => {
    const raw = currentSearchParams[param];
    const remaining = raw
      ? raw.split(',').filter(k => k !== key).join(',')
      : '';
    const next = { ...currentSearchParams };
    if (remaining) {
      next[param] = remaining;
    } else {
      delete next[param];
    }
    pushSearch(next);
  };

  const clearAll = () => {
    const next = { ...currentSearchParams };
    FILTER_QUERY_PARAMS.forEach(({ param }) => delete next[param]);
    pushSearch(next);
  };

  return (
    <div className={css.activeFilters}>
      <ul className={css.activeFiltersList}>
        {pills.map(p => (
          <li key={`${p.param}:${p.key}`} className={css.activeFilterPill}>
            <span className={css.activeFilterGroupLabel}>{p.groupLabel}:</span>
            <span className={css.activeFilterLabel}>{p.label}</span>
            <button
              type="button"
              className={css.activeFilterRemove}
              onClick={() => removePill(p.param, p.key)}
              aria-label={intl.formatMessage({ id: 'Posts.removeFilter' }, { label: p.label })}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className={css.activeFiltersClearAll} onClick={clearAll}>
        <FormattedMessage id="Posts.clearAllFilters" />
      </button>
    </div>
  );
};

// Header component with sort control and create post button
const PostsHeader = ({ intl, currentSearchParams, history, sortByOptions, initialSortValue }) => (
  <div className={css.sortByWrapper}>
    <SortBy
      label={intl.formatMessage({ id: 'Posts.sortBy' })}
      urlParam="sort"
      options={sortByOptions}
      initialValue={initialSortValue}
      onSelect={(urlParam, option) => {
        const newSearchParams = {
          ...currentSearchParams,
          [urlParam]: option,
        };
        history.push(
          createResourceLocatorString('CommunityPage', routeConfiguration(), {}, newSearchParams)
        );
      }}
    />
    <NamedLink className={css.createPostLink} name="NewPostPage">
      <FormattedMessage id="CommunityPage.createPostLink" />
    </NamedLink>
  </div>
);

// Post list component
const PostList = ({
  posts,
  currentUser,
  linkPreviews,
  categories,
  location,
  history,
  addPostCountInProgress,
  addPostCountError,
  onAddPostCount,
  onTranslate,
}) => (
  <div className={css.posts}>
    {posts.map(post => {
      const matchLinkPreview = linkPreviews.find(p => p.id.uuid === post.id.uuid);
      const linkPreview = matchLinkPreview ? matchLinkPreview.linkPreview : null;
      return (
        <Post
          key={post.id.uuid}
          post={post}
          currentUser={currentUser}
          linkPreview={linkPreview}
          categories={categories}
          location={location}
          history={history}
          addPostCountInProgress={addPostCountInProgress}
          addPostCountError={addPostCountError}
          onAddPostCount={onAddPostCount}
          onTranslate={onTranslate}
        />
      );
    })}
  </div>
);

// Main Posts component
const Posts = props => {
  const {
    rootClassName,
    className,
    intl,
    posts,
    currentUser,
    location,
    history,
    currentSearchParams,
    searchParamsForPagination,
    pagination,
    categories,
    topics,
    disciplines,
    roles,
    searchPostsInProgress,
    searchPostsError,
    addPostCountInProgress,
    addPostCountError,
    onAddPostCount,
    onTranslate,
    linkPreviews,
  } = props;

  // Handle loading and error states. Skeleton only when we have no
  // posts to show — post-hydration and filter-change refetches keep
  // the already-rendered feed on screen instead of flashing skeletons.
  if (searchPostsInProgress && (!posts || posts.length === 0)) {
    return <LoadingState />;
  } else if (searchPostsError) {
    return <ErrorState />;
  }

  // Check if posts are loaded and if there are any
  const postsAreLoaded = !searchPostsInProgress && pagination?.totalItems !== null;
  const hasListings = postsAreLoaded && pagination?.totalItems > 0;

  // Get current category
  const postsCategory = currentSearchParams.pub_category
    ? categories.find(c => c.key === currentSearchParams.pub_category)
    : null;

  // Create return link if needed
  const returnLink = postsAreLoaded && postsCategory ? <ReturnLink /> : null;

  // Handle empty state
  if (postsAreLoaded && !hasListings) {
    return <EmptyState returnLink={returnLink} postsCategory={postsCategory} />;
  }

  // Setup sorting options
  const sortByOptions = [
    {
      key: 'meta_voteCount',
      label: intl.formatMessage({ id: 'Posts.SortBy.popular' }),
    },
    {
      key: 'createdAt',
      label: intl.formatMessage({ id: 'Posts.SortBy.newest' }),
    },
    {
      key: '-createdAt',
      label: intl.formatMessage({ id: 'Posts.SortBy.oldest' }),
    },
  ];

  const initialSortValue =
    sortByOptions.find(option => option.key === currentSearchParams.sort)?.key ||
    sortByOptions[0].key;

  // Create pagination component if needed
  const paginationLinks =
    pagination?.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="CommunityPage"
        pageSearchParams={searchParamsForPagination}
        pagination={pagination}
      />
    ) : null;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <PostsHeader
        intl={intl}
        currentSearchParams={currentSearchParams}
        history={history}
        sortByOptions={sortByOptions}
        initialSortValue={initialSortValue}
      />

      <ActiveFilters
        intl={intl}
        currentSearchParams={currentSearchParams}
        history={history}
        optionGroups={{ categories, topics, disciplines, roles }}
      />

      <PostList
        posts={posts}
        currentUser={currentUser}
        linkPreviews={linkPreviews}
        categories={categories}
        location={location}
        history={history}
        addPostCountInProgress={addPostCountInProgress}
        addPostCountError={addPostCountError}
        onAddPostCount={onAddPostCount}
        onTranslate={onTranslate}
      />

      {paginationLinks}
    </div>
  );
};

export default Posts;
